import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdService } from "../../services/Ad.service";
import { useI18n } from "../../i18n";

/**
 * Admin paneldən yaradılan reklamlar.
 *  - merchantId verilsə → yalnız həmin mağazanın reklamları
 *  - verilməsə → bütün aktiv reklamlar (ana səhifə)
 *
 * Reklam yoxdursa komponent heç nə göstərmir (səhifədə boşluq qalmır).
 */
const AdsSection = ({ merchantId, limit = 6, variant = "light" }) => {
  const { t, tr } = useI18n();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await AdService.list({ merchantId, limit });
        if (!cancelled) setAds(data.ads || []);
      } catch {
        // Reklam ikinci dərəcəli məzmundur — xəta olsa səssizcə gizlədilir
        if (!cancelled) setAds([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [merchantId, limit]);

  if (loading || ads.length === 0) {
    return null;
  }

  const titleClass =
    variant === "light" ? "text-gray-800" : "text-white drop-shadow";

  return (
    <section className="mt-10">
      <h2 className={`text-2xl font-extrabold mb-4 ${titleClass}`}>
        {t("ads.title")}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {ads.map((ad) => {
          const card = (
            <article className="h-full bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
              {ad.image ? (
                <img
                  src={ad.image}
                  alt={tr(ad.title)}
                  loading="lazy"
                  className="w-full h-40 object-cover"
                />
              ) : (
                <div className="w-full h-40 bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
                  <span className="text-5xl">📣</span>
                </div>
              )}

              <div className="p-5 flex-1 flex flex-col text-left">
                <h3 className="text-lg font-bold text-gray-800">
                  {tr(ad.title)}
                </h3>

                {ad.merchant?.name && (
                  <p className="text-xs font-semibold text-indigo-500 mt-1">
                    🏪 {tr(ad.merchant.name)}
                  </p>
                )}

                {ad.content && (
                  <div
                    className="text-sm text-gray-600 mt-2 line-clamp-3 [&_p]:m-0"
                    dangerouslySetInnerHTML={{ __html: tr(ad.content) }}
                  />
                )}

                {ad.ends_at && (
                  <p className="text-xs text-gray-400 mt-auto pt-3">
                    {t("ads.until", {
                      date: new Date(ad.ends_at).toLocaleDateString(),
                    })}
                  </p>
                )}
              </div>
            </article>
          );

          // Mağazaya aid reklam → mağaza səhifəsinə keçid
          return ad.merchant_id && !merchantId ? (
            <Link key={ad.id} to={`/store/${ad.merchant_id}`} className="block h-full">
              {card}
            </Link>
          ) : (
            <div key={ad.id} className="h-full">
              {card}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AdsSection;
